/**
 * Xenial portal data service
 */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { forEach, get, head, isEmpty, merge } from 'lodash';

import {
  Observable,
  observableEMPTY,
  observableThrowError,
  rx,
} from '@shared/rx';
import { COMPANY_ID, SITE_TOKEN } from '@config/global-constants';
import {
  BackendApiType,
  EHeaderName,
  IApiListResponse,
  IEvent,
  ICompany,
  IEventStatus,
  IFilterQuery,
  ISite,
  ISiteRequestBody,
} from '@shared/types';
import { buildServiceIdentifyContext } from '@app/shared/utils/http-context.utils';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';
import { lazyInject } from '@app/shared/decorators';
import { I18n } from '@app/i18n/strings-keys';

import { environment } from '../../../../environments/environment';
import { TokenSessionService } from '../session';
import { createRequestParams } from '@app/shared/utils/request.utils';
import { NavigateService } from '../navigate/navigate.service';
import { I18nService } from '../i18n/i18n.service';

@Injectable()
export class XprtDataService {
  @lazyInject(HttpClient) public http: HttpClient;
  @lazyInject(TokenSessionService)
  public tokenSessionService: TokenSessionService;
  @lazyInject(NavigateService) public navigateService: NavigateService;
  @lazyInject(I18nService) protected i18nService: I18nService;

  public static UTILITY_TYPE: string = 'XprtDataService';
  public defaultQuery: IFilterQuery = {};

  private get baseUrl(): string {
    return environment.xprtBackendURI;
  }

  private get token(): string {
    return this.tokenSessionService.getToken(SITE_TOKEN);
  }

  private get companyId(): string {
    return this.tokenSessionService.getToken(COMPANY_ID);
  }

  private get headers(): HttpHeaders {
    return new HttpHeaders({
      [EHeaderName.Authorization]: `Bearer ${this.token}`,
      [EHeaderName.XCompanyId]: this.companyId,
    });
  }

  public checkToken(token: string): Observable<any> {
    const headers = new HttpHeaders({
      [EHeaderName.Authorization]: `Bearer ${token}`,
    });

    return this.http
      .post(`${this.baseUrl}/v1/token/verify`, {}, { headers })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public getEventDetailById(eventId: string): Observable<IEventStatus[]> {
    let params = new HttpParams();
    params = params.set('$filter', `_id eq in(${eventId})`);
    params = params.set('$select', 'status');

    return this.http
      .get(
        `${this.baseUrl}/v1/companies/${this.tokenSessionService.companyId}/events`,
        {
          params,
          headers: this.headers,
        },
      )
      .pipe(
        rx.map((response: IApiListResponse<IEvent>) =>
          get(response, 'items', []),
        ),
        this.catchHttpErrorAndPickErrorList(),
      );
  }

  public fetchSitesList(userQuery?: IFilterQuery): Observable<ISite[]> {
    const basicPath = `${this.baseUrl}/v1/companies/${this.tokenSessionService.companyId}/get-sites`;
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const [requestQuery, requestBody] = this.getRequestParameters(query);
    let params = this.createHttpParams({ ...query, ...requestQuery });

    return this.http
      .post(basicPath, requestBody, {
        params,
        headers: this.headers,
        context: buildServiceIdentifyContext(ENTITY_TYPES.SITE),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<ISite>) =>
          get(response, 'items', []),
        ),
      );
  }

  public fetchSiteDetail(userQuery?: IFilterQuery): Observable<ISite> {
    const basicPath = `${this.baseUrl}/v1/sites/${this.tokenSessionService.siteId}`;
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const [requestQuery] = this.getRequestParameters(query);
    let params = this.createHttpParams({ ...query, ...requestQuery });

    return this.http
      .get<ISite>(basicPath, { params, headers: this.headers })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public fetchEventList(userQuery?: IFilterQuery): Observable<IEvent[]> {
    const basicPath = `${this.baseUrl}/v1/companies/${this.tokenSessionService.companyId}/events`;
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    let params = this.createHttpParams(query);

    return this.http
      .get(basicPath, {
        params,
        headers: this.headers,
        context: buildServiceIdentifyContext(ENTITY_TYPES.EVENTS),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IEvent>) =>
          get(response, 'items', []),
        ),
      );
  }

  public createHttpParams(query: IFilterQuery): HttpParams {
    let params = new HttpParams();
    forEach(query, (param, key) => {
      if (key === 'top') {
        params = params.set('$top', param.toString());
      } else if (key === 'select' && !isEmpty(param)) {
        params = params.set('$select', param.toString());
      } else if (key === 'filter' && !isEmpty(param)) {
        params = params.set('$filter', param.toString());
      } else {
        params = params.set(key, param.toString());
      }
    });

    return params;
  }

  private getRequestParameters(
    query: IFilterQuery,
  ): [IFilterQuery, ISiteRequestBody] {
    const { select } = query;
    const needKey = ['id', 'name', 'store_number'];

    query.select = select ? select : needKey;

    let requestBody: ISiteRequestBody;
    const searchQuery: IFilterQuery = { ...query };
    const odataFilter = get(searchQuery, 'filter');

    if (odataFilter) {
      delete searchQuery.filter;
      requestBody = {
        'x-odata-filter': `$filter=${odataFilter}`,
      };
    }

    return [searchQuery, requestBody];
  }

  public getCompanyById(
    companyId: string,
    query?: IFilterQuery,
  ): Observable<ICompany> {
    const basicPath = `${this.baseUrl}/v1/companies/${companyId}`;

    const params = this.createParams(query);

    return this.http
      .get(basicPath, {
        params,
        headers: this.headers,
      })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  /**
   * will return empty and redirect to error page with NO_EVENT error when not exist
   * @param userQuery user query
   * @returns
   */
  public checkExistEvent(userQuery?: IFilterQuery): Observable<IEvent> {
    const defaultQuery: IFilterQuery = {
      filter: `status eq 'open'`,
      select: ['id', 'name', 'event_days'],
    };

    return this.fetchEventList({
      top: 1,
      ...defaultQuery,
      ...userQuery,
    }).pipe(
      rx.map((events: IEvent[]) => head(events) as IEvent),
      rx.tap((event) => {
        if (isEmpty(event)) {
          this.navigateService.navigateToError(
            this.i18nService.instant(I18n.EventStrings.NoEvent),
          );

          return observableEMPTY;
        }
      }),
    );
  }

  /**
   * Method which creates HttpParams and returns
   */
  public createParams(query: IFilterQuery = {}): HttpParams {
    return createRequestParams({
      ...query,
      config: {
        backendApiType: BackendApiType.PORTAL,
      },
    });
  }

  private catchHttpErrorAndPickErrorList =
    (): any =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) =>
          observableThrowError(get(error, 'errors')),
        ),
      );
}
